import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { AxiosResponse } from 'axios';
import { TbMailbox } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { authorizedAxiosInstance, unauthorizedAxiosInstance } from '@/clients';
import { isApiServiceError } from '@/services/http/errors';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { nanoId } from '@/utilities';

import {
    InvitationActions,
    InvitationState,
    ResendWorkspaceInvitationResponse,
    WorkspaceInvitationSummary,
} from '../types';

export const createInvitationActions: StateCreator<
    InvitationState & InvitationActions,
    [['zustand/immer', never]],
    [],
    InvitationActions
> = (set, get) => ({
    // Core
    fetchInvitations: async (params) => {
        set({ loading: true });

        try {
            const res: AxiosResponse = await authorizedAxiosInstance.get('/api/invitations', { params });

            if (res.status === 200) {
                set({ invitations: res.data, loading: false, error: undefined });
            }
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load invitations' });
        }
    },
    fetchWorkspaceInvitations: async () => {
        set((state) => {
            state.loadingWorkspaceInvitations = true;
            state.error = undefined;
        });

        try {
            const res: AxiosResponse<WorkspaceInvitationSummary[]> =
                await authorizedAxiosInstance.get('/api/invitations/workspace');

            set((state) => {
                state.workspaceInvitations = res.data;
                state.loadingWorkspaceInvitations = false;
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.loadingWorkspaceInvitations = false;
                state.error = 'Failed to load workspace invitations';
            });
        }
    },
    fetchInvitationById: async (id) => {
        set((state) => {
            state.loadingInvitation = true;
            state.error = undefined;
        });

        try {
            const res = await authorizedAxiosInstance.get(`/api/invitations/${id}`);

            set((state) => {
                state.selectedInvitation = res.data;
                state.loadingInvitation = false;
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch invitation detail';
                state.loadingInvitation = false;
            });
        }
    },
    clearSelectedInvitation: () => {
        set((state) => {
            state.selectedInvitation = null;
        });
    },
    createInvitation: async (input) => {
        set((state) => {
            state.loadingInvitation = true;
            state.error = undefined;
        });

        try {
            const res: AxiosResponse<WorkspaceInvitationSummary> = await authorizedAxiosInstance.post(
                '/api/invitations',
                input,
            );

            set((state) => {
                state.workspaceInvitations = [
                    res.data,
                    ...state.workspaceInvitations.filter((invitation) => invitation.id !== res.data.id),
                ];
                state.loadingInvitation = false;
            });

            toast(`Invitation Sent`, {
                icon: createElement(TbMailbox, { size: '1rem' }),
                description: res.data.emailAddress,
            });

            return res.data;
        } catch (error) {
            captureException(error);

            set((state) => {
                state.loadingInvitation = false;
                state.error = 'Failed to create invitation';
            });

            throw error;
        }
    },
    updateInvitation: async (id, updates) => {
        const prev = get().invitations.find((f) => f.id === id);
        if (!prev) {
            return;
        }
        const prevSnapshot = { ...prev };

        set((state) => {
            const f = state.invitations.find((f) => f.id === id);
            if (f) {
                Object.assign(f, updates);
            }

            // Also update selectedInvitation if it matches
            if (state.selectedInvitation?.id === id) {
                Object.assign(state.selectedInvitation, updates);
            }
        });

        try {
            const res = await authorizedAxiosInstance.put(`/api/invitations/${id}`, updates);

            if (res.status === 200) {
                set((state) => {
                    const f = state.invitations.find((f) => f.id === id);
                    if (f) {
                        Object.assign(f, res.data);
                    }

                    if (state.selectedInvitation?.id === id) {
                        Object.assign(state.selectedInvitation, res.data);
                    }
                });
            }
        } catch (error) {
            captureException(error);

            set((state) => {
                const f = state.invitations.find((f) => f.id === id);
                if (f) {
                    Object.assign(f, prevSnapshot);
                }

                if (state.selectedInvitation?.id === id) {
                    Object.assign(state.selectedInvitation, prevSnapshot);
                }

                state.error = 'Failed to update invitation';
            });

            throw error;
        }
    },
    deleteInvitation: async (id) => {
        const prev = [...get().invitations];
        const prevSelectedInvitation = get().selectedInvitation;

        set((state) => {
            state.invitations = state.invitations.filter((f) => f.id !== id);

            // Clear selectedInvitation if it's the deleted one
            if (state.selectedInvitation?.id === id) {
                state.selectedInvitation = null;
            }
        });

        try {
            await authorizedAxiosInstance.delete(`/api/invitations/${id}`);
        } catch (error) {
            captureException(error);

            set({
                invitations: prev,
                selectedInvitation: prevSelectedInvitation,
                error: 'Failed to delete invitation',
            });

            throw error;
        }
    },
    revokeWorkspaceInvitation: async (id) => {
        const previousWorkspaceInvitations = [...get().workspaceInvitations];

        set((state) => {
            state.workspaceInvitations = state.workspaceInvitations.filter((invitation) => invitation.id !== id);
        });

        try {
            await authorizedAxiosInstance.delete(`/api/invitations/workspace/${id}`);
        } catch (error) {
            captureException(error);

            set((state) => {
                state.workspaceInvitations = previousWorkspaceInvitations;
                state.error = 'Failed to revoke workspace invitation';
            });

            throw error;
        }
    },
    resendWorkspaceInvitation: async (id) => {
        try {
            const res = await authorizedAxiosInstance.post<ResendWorkspaceInvitationResponse>(
                `/api/invitations/workspace/${id}/resend`,
                {},
                { suppressErrorCapture: true },
            );

            set((state) => {
                const invitation = state.workspaceInvitations.find((candidate) => candidate.id === id);
                if (invitation) {
                    invitation.sentAt = res.data.sentAt;
                }
            });

            return res.data;
        } catch (error) {
            if (!isApiServiceError(error) || error.code !== 'RATE_LIMITED') {
                captureException(error);
            }
            throw error;
        }
    },
    // Additions
    resolveInvitationByToken: async (token) => {
        set({ invitationResolution: null, resolving: true, error: undefined });

        try {
            const res = await unauthorizedAxiosInstance.post('/auth/invitations/resolve', { token });

            set({
                invitationResolution: res.data,
                resolving: false,
            });
        } catch (error) {
            captureException(error);
            set({ invitationResolution: null, resolving: false, error: 'Failed to resolve invitation' });
        }
    },
    acceptInvitationByToken: async (token) => {
        set({ accepting: true, error: undefined });

        try {
            const res = await unauthorizedAxiosInstance.post(
                '/auth/invitations/accept',
                { token },
                {
                    headers: {
                        'Idempotency-Key': nanoId(),
                    },
                },
            );

            const data = res.data;

            if (data.action === 'MAGIC_LINK_SENT') {
                await unauthorizedAxiosInstance.post('/auth/magic', {
                    destination: data?.emailAddress,
                });

                set({ accepting: false });

                return data;
            }

            if (data.action === 'SEAT_FULL') {
                toast.error('Workspace is Full');

                set((state) => {
                    state.accepting = false;
                    state.invitationResolution = state.invitationResolution
                        ? { ...state.invitationResolution, seatsAvailable: false }
                        : state.invitationResolution;
                });

                return data;
            }

            if (data.action === 'JOINED' || data.action === 'ALREADY_MEMBER') {
                window.location.href = '/';

                return data;
            }

            set({ accepting: false });

            return data;
        } catch (error) {
            captureException(error);

            set({ accepting: false, error: 'Failed to accept invitation' });

            throw error;
        }
    },
    rejectInvitationByToken: async (token) => {
        set({ rejecting: true, error: undefined });

        try {
            await unauthorizedAxiosInstance.post(
                '/auth/invitations/reject',
                { token },
                {
                    headers: {
                        'Idempotency-Key': nanoId(),
                    },
                },
            );

            set((state) => {
                state.rejecting = false;
                state.invitationResolution = state.invitationResolution
                    ? { ...state.invitationResolution, status: 'revoked' }
                    : state.invitationResolution;
            });
        } catch (error) {
            captureException(error);

            set({ rejecting: false, error: 'Failed to reject invitation' });

            throw error;
        }
    },
    // For Authenticated Useres
    acceptInvitationById: async (invitationId) => {
        const prev = [...get().invitations];

        set((state) => {
            state.accepting = true;
            state.error = undefined;
            state.invitations = state.invitations.filter((i) => i.id !== invitationId);
        });

        try {
            const res = await authorizedAxiosInstance.post(
                `/api/invitations/${invitationId}/accept`,
                {},
                {
                    headers: {
                        'Idempotency-Key': nanoId(),
                    },
                },
            );

            const data = res.data;

            set({ accepting: false });

            if (data.action === 'JOINED' || data.action === 'ALREADY_MEMBER') {
                window.location.href = '/';

                return data;
            }

            set({ invitations: prev });

            return data;
        } catch (error) {
            captureException(error);
            set({ accepting: false, invitations: prev, error: 'Failed to accept invitation' });

            throw error;
        }
    },
});
